import React, { useEffect } from 'react';
import Container from '../components/common/Container';
import Footer from '../components/layout/Footer';
import Navbar from '../components/layout/Navbar';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Fixed Navbar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <Container>
          <Navbar />
        </Container>
      </div>

      {/* Content with padding to account for fixed navbar */}
      <div className="pt-24">
        {/* Clean header section */}
        <div className="relative py-16 sm:py-24">
          <Container>
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 mb-6">
                Terms of Service
              </h1>
              <p className="text-lg text-gray-600 mb-2">
                Our commitment to transparent and fair service terms
              </p>
              <p className="text-sm text-gray-500">
                Effective Date: August 5, 2025
              </p>
            </div>
          </Container>
        </div>

        {/* Main content */}
        <div className="pb-16 sm:pb-24">
          <Container>
            <div className="max-w-4xl mx-auto">
              {/* Important notice */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-12">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800 mb-2">Important Notice</h3>
                    <p className="text-sm text-red-700 leading-relaxed">
                      BY SUBMITTING AN APPLICATION OR BY ACCESSING OR USING ANY PART OF THE SERVICE, YOU (A) ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS; (B) REPRESENT AND WARRANT THAT YOU ARE AT LEAST EIGHTEEN (18) YEARS OF AGE AND HAVE THE LEGAL CAPACITY TO ENTER INTO THIS AGREEMENT; AND (C) AGREE TO BE BOUND BY OUR PRIVACY POLICY. IF YOU DO NOT AGREE TO ALL OF THE TERMS AND CONDITIONS OF THIS AGREEMENT, YOU MUST NOT ACCESS OR USE THE SERVICE.
                    </p>
                  </div>
                </div>
              </div>

              {/* Welcome section */}
              <div className="prose prose-lg prose-gray max-w-none mb-12">
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    Welcome to Burnt, the private professional network for top performers ("Burnt," "we," "us," or "our"). These Terms of Service ("Terms") govern your application for membership and your access to and use of our services.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    These Terms constitute a legally binding agreement between you ("You" or "Member") and Burnt Inc., a Delaware corporation ("Burnt," "we," "us," or "our"). These Terms govern your application for membership and your access to and use of the Burnt private professional network, including our website located at justburnt.com (the "Site"), and all related content, features, events, and services (collectively, the "Service").
                  </p>
                </div>
              </div>

              {/* Terms sections */}
              <div className="space-y-12">
                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">1. The Service</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <p>
                      The Service provides approved Members with access to a private, curated professional network designed to facilitate connections, career advancement, and knowledge sharing among top performers. The Service includes, without limitation:
                    </p>
                    
                    <div className="grid gap-4 sm:gap-6">
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                        <h3 className="font-semibold text-purple-900 mb-2">(a) Professional Networking Platform</h3>
                        <p className="text-purple-800 text-sm leading-relaxed">A platform for professional networking with other Members</p>
                      </div>
                      
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <h3 className="font-semibold text-blue-900 mb-2">(b) Private Forums and Discussion Boards</h3>
                        <p className="text-blue-800 text-sm leading-relaxed">Private forums and discussion boards for sharing content and insights</p>
                      </div>
                      
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <h3 className="font-semibold text-green-900 mb-2">(c) Curated Career Opportunities</h3>
                        <p className="text-green-800 text-sm leading-relaxed">Access to curated career opportunities from our partner companies ("Employers")</p>
                      </div>
                      
                      <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                        <h3 className="font-semibold text-indigo-900 mb-2">(d) Exclusive Events</h3>
                        <p className="text-indigo-800 text-sm leading-relaxed">Invitations to exclusive virtual and in-person events ("Events")</p>
                      </div>
                    </div>

                    <p>
                      We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice to You.
                    </p>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">2. Membership</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <p>
                      Access to the Service is by application only and is granted at Burnt's sole and absolute discretion. Membership is a limited, non-transferable, revocable privilege, not a right. We reserve the right to grant, deny, suspend, or revoke your membership at any time, for any reason, including but not limited to a violation of these Terms or a failure to adhere to our community standards.
                    </p>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">3. Member Conduct and Content</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">3.1. Member Content</h3>
                    <p>
                      You are solely responsible for all information, data, text, comments, posts, and other materials that You submit, post, or display on or through the Service ("Member Content"). You represent and warrant that You own all rights, title, and interest in and to your Member Content or have otherwise secured all necessary rights and permissions to submit it to the Service.
                    </p>

                    <h3 className="text-lg font-semibold text-gray-900 mb-4">3.2. License Grant to Burnt</h3>
                    <p>
                      You hereby grant to Burnt a worldwide, non-exclusive, perpetual, irrevocable, royalty-free, fully paid-up, sublicensable, and transferable license to use, host, store, reproduce, modify, publish, display, distribute, and create derivative works from your Member Content in connection with operating, providing, and promoting the Service.
                    </p>

                    <h3 className="text-lg font-semibold text-gray-900 mb-4">3.3. Prohibited Conduct</h3>
                    <p>You agree that You will not, directly or indirectly:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>(a) Use the Service for any unlawful, fraudulent, or malicious purpose</li>
                      <li>(b) Post, upload, or distribute any Member Content that is defamatory, obscene, harassing, abusive, hateful, discriminatory, or that infringes upon any third party's intellectual property or privacy rights</li>
                      <li>(c) Impersonate any person or entity, or falsely state or otherwise misrepresent your affiliation with a person or entity</li>
                      <li>(d) Engage in any form of unauthorized advertising, spam, or commercial solicitation</li>
                      <li>(e) Distribute viruses, worms, or any other malicious or disruptive code</li>
                      <li>(f) Scrape, crawl, or use any other automated means to access the Service or extract data</li>
                      <li>(g) Reverse engineer, decompile, or otherwise attempt to discover the source code of the Service</li>
                      <li>(h) Interfere with or disrupt the integrity or performance of the Service or its community</li>
                    </ul>

                    <h3 className="text-lg font-semibold text-gray-900 mb-4">3.4. Our Right to Moderate</h3>
                    <p>
                      Burnt does not have an obligation to monitor Member Content but reserves the right to review, screen, edit, or remove any Member Content, and to suspend or terminate any Member, at any time, for any reason, in its sole discretion, without notice and without liability.
                    </p>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">4. Events</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">4.1. Code of Conduct</h3>
                    <p>
                      You agree to adhere to all posted rules and to conduct yourself professionally at all Events. Burnt reserves the right to remove You from any Event for behavior it deems disruptive, unsafe, or unprofessional.
                    </p>

                    <h3 className="text-lg font-semibold text-gray-900 mb-4">4.2. Assumption of Risk and Release of Liability</h3>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                      <p className="text-yellow-800 font-semibold uppercase text-sm">
                        YOUR PARTICIPATION IN ANY EVENT IS VOLUNTARY. YOU HEREBY ASSUME ALL RISKS, KNOWN AND UNKNOWN, ASSOCIATED WITH YOUR ATTENDANCE AND PARTICIPATION. TO THE FULLEST EXTENT PERMITTED BY LAW, YOU HEREBY RELEASE, WAIVE, AND FOREVER DISCHARGE BURNT AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS FROM ANY AND ALL CLAIMS, DEMANDS, LIABILITIES, OR CAUSES OF ACTION ARISING OUT OF OR RELATED TO ANY LOSS, DAMAGE, OR INJURY, INCLUDING DEATH, THAT MAY BE SUSTAINED BY YOU IN CONNECTION WITH ANY EVENT.
                      </p>
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-4">4.3. Media Release</h3>
                    <p>
                      You hereby grant Burnt the irrevocable right and permission to photograph, film, and record You at Events and to use such media in any and all promotional and marketing materials, worldwide, in perpetuity, without compensation.
                    </p>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">5. Intellectual Property</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <p>
                      Excluding your Member Content, the Service and all materials therein, including software, text, graphics, logos, patents, trademarks, and service marks (the "Burnt IP"), are the exclusive property of Burnt and its licensors. You agree not to sell, license, rent, modify, distribute, or otherwise use the Burnt IP except as expressly permitted by these Terms.
                    </p>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">6. Disclaimers of Warranties</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                      <p className="text-red-800 font-semibold uppercase text-sm">
                        THE SERVICE IS PROVIDED ON AN "AS IS," "AS AVAILABLE," AND "WITH ALL FAULTS" BASIS. TO THE FULLEST EXTENT PERMITTED BY LAW, BURNT EXPRESSLY DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. BURNT MAKES NO WARRANTY THAT THE SERVICE WILL MEET YOUR REQUIREMENTS, BE UNINTERRUPTED, ERROR-FREE, OR SECURE. WE DISCLAIM ALL LIABILITY FOR THE CONDUCT OF OTHER MEMBERS, EMPLOYERS, OR THIRD PARTIES.
                      </p>
                    </div>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">7. Limitation of Liability</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                      <p className="text-red-800 font-semibold uppercase text-sm">
                        TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT SHALL BURNT INC. OR ITS AFFILIATES, OFFICERS, DIRECTORS, OR EMPLOYEES BE LIABLE FOR ANY INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING WITHOUT LIMITATION DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO YOUR USE OF THE SERVICE OR YOUR PARTICIPATION IN ANY EVENT. BURNT'S TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT OF FEES PAID BY YOU TO BURNT IN THE TWELVE (12) MONTHS PRIOR TO THE CLAIM (IF ANY), OR (B) ONE HUNDRED U.S. DOLLARS ($100.00).
                      </p>
                    </div>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">8. Indemnification</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <p>
                      You agree to defend, indemnify, and hold harmless Burnt Inc. and its officers, directors, employees, and agents from and against any and all claims, damages, obligations, losses, liabilities, costs, or debt, and expenses (including but not limited to attorney's fees) arising from: (a) your use of and access to the Service; (b) your violation of any term of these Terms; (c) your violation of any third-party right, including any intellectual property or privacy right; or (d) any claim that your Member Content caused damage to a third party.
                    </p>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">9. Governing Law, Arbitration, and Class Action Waiver</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">9.1. Governing Law</h3>
                    <p>
                      These Terms shall be governed by the internal substantive laws of the State of Delaware, without respect to its conflict of laws principles.
                    </p>

                    <h3 className="text-lg font-semibold text-gray-900 mb-4">9.2. Arbitration</h3>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                      <p className="text-yellow-800 font-semibold uppercase text-sm">
                        YOU AGREE THAT ANY AND ALL DISPUTES ARISING FROM OR RELATING TO THE SUBJECT MATTER OF THESE TERMS SHALL BE FINALLY SETTLED BY BINDING ARBITRATION IN NEW CASTLE COUNTY, DELAWARE, IN ACCORDANCE WITH THE COMMERCIAL ARBITRATION RULES OF THE AMERICAN ARBITRATION ASSOCIATION (AAA).
                      </p>
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-4">9.3. Class Action and Jury Trial Waiver</h3>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                      <p className="text-yellow-800 font-semibold uppercase text-sm">
                        ALL CLAIMS MUST BE BROUGHT IN THE PARTIES' INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS ACTION, COLLECTIVE ACTION, OR REPRESENTATIVE PROCEEDING. YOU AGREE TO WAIVE YOUR RIGHT TO A JURY TRIAL.
                      </p>
                    </div>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">10. Miscellaneous</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <p>
                      These Terms, together with the Privacy Policy, constitute the entire agreement between You and Burnt. If any provision is found to be unenforceable, the remaining provisions will remain in full force and effect. These Terms may not be assigned by You without our prior written consent, but may be assigned by Burnt without restriction. No waiver of any term shall be deemed a further or continuing waiver of such term. All notices to Burnt must be sent to the contact information below and are deemed given upon receipt.
                    </p>
                    
                    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                      <address className="text-gray-700 not-italic leading-relaxed">
                        <strong>Burnt Inc.</strong><br />
                        Attn: Legal Department<br />
                        1111 25th St NW #410<br />
                        Washington DC 20037<br />
                        Email: <a href="mailto:legal@justburnt.com" className="text-purple-600 hover:text-purple-700 underline">legal@justburnt.com</a>
                      </address>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </Container>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default TermsOfService;